import { useEffect, useRef } from "react";

import { IconSearch } from "./icons";

interface QueryBoxProps {
  value: string;
  onChange: (q: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
}

/**
 * Search input + submit button, styled to match the Arty design tokens.
 * Disabled while a search or vote is in flight; empty/whitespace is not
 * accepted (submit button disables, form keeps emitting submit which
 * we short-circuit).
 */
export function QueryBox({
  value,
  onChange,
  onSubmit,
  disabled,
  autoFocus,
  placeholder,
}: QueryBoxProps) {
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (autoFocus && ref.current) ref.current.focus();
  }, [autoFocus]);

  const canSubmit = !!value.trim() && !disabled;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit();
      }}
      style={{ position: "relative" }}
    >
      <span
        style={{
          position: "absolute",
          left: 16,
          top: "50%",
          transform: "translateY(-50%)",
          color: "var(--muted-foreground)",
          pointerEvents: "none",
        }}
      >
        <IconSearch size={18} />
      </span>
      <input
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder ?? "Ask what you'd actually ask Know…"}
        style={{
          width: "100%",
          height: 54,
          padding: "0 160px 0 46px",
          fontSize: 15,
          border: "1px solid var(--border)",
          borderRadius: 12,
          background: "var(--background)",
          fontFamily: "var(--font-sans)",
          color: "var(--foreground)",
          outline: "none",
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          boxSizing: "border-box",
        }}
      />
      <button
        type="submit"
        disabled={!canSubmit}
        style={{
          position: "absolute",
          right: 8,
          top: 8,
          height: 38,
          padding: "0 14px",
          background: canSubmit ? "var(--atf-pink-600)" : "var(--secondary)",
          color: canSubmit ? "white" : "var(--muted-foreground)",
          border: "none",
          borderRadius: 8,
          cursor: canSubmit ? "pointer" : "not-allowed",
          fontFamily: "var(--font-sans)",
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        Run blind search
      </button>
    </form>
  );
}

export const SAMPLE_QUERIES: readonly string[] = [
  "digital transformation roadmap",
  "gen-ai platform architecture",
  "retail transformation levers",
  "data mesh at scale",
  "mlops maturity model",
  "conseil marketing 2024",
];
